import { Box, Heading, Modal, Paragraph } from 'component-library';
import { useTranslation } from 'react-i18next';
import { InputField } from 'component-library/components/Input/Text/InputField';
import type { ChangeEvent } from 'react';

export type ContainerTemplateModalKind = 'create' | 'delete' | 'replace';

export const ContainerTemplateModal = ({
  modal,
  name,
  busy,
  disabled,
  hasContainers,
  failed,
  onNameChange,
  onCancel,
  onConfirm,
}: {
  modal: ContainerTemplateModalKind | null;
  name: string;
  busy: boolean;
  disabled: boolean;
  hasContainers: boolean;
  failed: boolean;
  onNameChange: (name: string) => void;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const content = {
    create: {
      title: t('container-templates.create.title'),
      description: t('container-templates.create.description'),
      confirm: t('container-templates.create.save'),
    },
    replace: {
      title: t('container-templates.replace.title'),
      description: t('container-templates.replace.confirmation'),
      confirm: t('container-templates.replace.confirm'),
    },
    delete: {
      title: t('container-templates.delete.title'),
      description: t('container-templates.delete.confirmation'),
      confirm: t('container-templates.delete.confirm'),
    },
  }[modal ?? 'create'];

  return (
    <Modal
      open={modal !== null}
      disableClose={busy}
      maxWidth="650px"
      onClose={() => {
        if (!busy) onCancel();
      }}
      secondaryAction={{
        label: t('container-templates.cancel'),
        disabled: busy,
        action: onCancel,
      }}
      primaryAction={{
        label: content.confirm,
        disabled: disabled || (modal === 'create' && (!name.trim() || !hasContainers)),
        isLoading: busy,
        action: onConfirm,
      }}
    >
      <Heading as="h2" variant="large">
        {content.title}
      </Heading>
      <Box mt="small" mb="medium">
        <Paragraph>{content.description}</Paragraph>
      </Box>
      {modal === 'create' && (
        <InputField
          label={t('container-templates.name.label')}
          value={name}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            onNameChange(event.target.value);
          }}
        />
      )}
      {failed && (
        <div role="alert">
          <Paragraph>{t('container-templates.error')}</Paragraph>
        </div>
      )}
    </Modal>
  );
};
